import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Package } from "lucide-react";
import api from "../lib/api";
import { money, formatDateTime } from "../lib/format";
import SmartImage from "../components/SmartImage";
import { ErrorState, PageLoader } from "../components/States";
import useSeo from "../hooks/useSeo";

export default function OrderConfirm() {
  useSeo({ title: "Order confirmation - NQ Springs & 4x4 Accessories", noindex: true });
  const { id } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data.order))
      .catch(() => setError(true));
  }, [id]);

  if (error)
    return (
      <div className="container-nq section-nq">
        <ErrorState title="Order not found" />
      </div>
    );
  if (!order) return <PageLoader label="Loading your order" />;

  const paid = order.payment_status === "paid" || params.get("paid") === "1";

  return (
    <div className="container-nq section-nq" data-testid="order-page">
      <div className="mx-auto max-w-2xl">
        <div className="card-nq p-8 text-center">
          {paid ? (
            <CheckCircle2 className="mx-auto h-12 w-12 gold-text" />
          ) : (
            <Clock className="mx-auto h-12 w-12 gold-text" />
          )}
          <h1 className="mt-4 text-2xl font-bold">
            {paid ? "Thanks for your order" : "Order received"}
          </h1>
          <p className="mt-2 text-sm text-[color:var(--nq-stone)]">
            Reference <span className="font-mono gold-text">{order.reference}</span>
            {order.created_at ? ` · ${formatDateTime(order.created_at)}` : ""}
          </p>
          <p className="mt-3 text-sm text-[color:var(--nq-stone)]">
            {paid
              ? "We've received your payment and will get your order on its way."
              : "Your order is recorded. Our team will contact you to finalise payment."}
          </p>
        </div>

        <div className="card-nq mt-6 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Package className="h-5 w-5 gold-text" /> Order details
          </h2>

          <div className="mt-4 space-y-3">
            {(order.items || []).map((l) => (
              <div key={l.slug} className="flex items-center gap-3">
                <div className="w-14 shrink-0">
                  <SmartImage src={l.image} alt={l.name} ratio="aspect-square" fit="contain" fallbackLabel={l.name} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{l.name}</p>
                  <p className="text-xs text-[color:var(--nq-stone)]">Qty {l.qty}</p>
                </div>
                <p className="font-mono text-sm">{money(l.line_total)}</p>
              </div>
            ))}
          </div>

          <dl className="mt-5 space-y-2 border-t pt-4 text-sm" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <div className="flex justify-between">
              <dt className="text-[color:var(--nq-stone)]">Subtotal</dt>
              <dd className="font-mono">{money(order.subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[color:var(--nq-stone)]">Shipping ({order.shipping_method})</dt>
              <dd className="font-mono">{order.shipping === 0 ? "FREE" : money(order.shipping)}</dd>
            </div>
            <div className="flex justify-between border-t pt-2.5 text-base font-bold" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <dt>Total</dt>
              <dd className="font-mono gold-text">{money(order.total)}</dd>
            </div>
          </dl>

          <div className="mt-5 border-t pt-4 text-xs text-[color:var(--nq-stone)]" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <p className="font-semibold text-[color:var(--nq-sand)]">Delivering to</p>
            <p className="mt-1">
              {order.customer?.name}
              {order.customer?.address1 ? `, ${order.customer.address1}` : ""}
              {order.customer?.suburb ? `, ${order.customer.suburb}` : ""}
              {order.customer?.state ? ` ${order.customer.state}` : ""}
              {order.customer?.postcode ? ` ${order.customer.postcode}` : ""}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-3">
          <Link to="/shop" className="btn-gold">Keep shopping</Link>
          <Link to="/" className="btn-outline-nq">Back home</Link>
        </div>
      </div>
    </div>
  );
}
